import React, { FC } from 'react';
import { Icon, Icons } from '@silverfort/sf-components';
import { bigNumberToText } from '../../../../components/utils';
import { useIspmContext } from '../../IspmContext';
import { getRiskIcon } from '../../../../consts/Insight';
import { SEVERITIES_INFO } from '../../../../consts/Severity';
import { getRiskIndicatorByRiskRule, RISK_SEVERITY } from '@silverfort-nodelibs/common/utils/insight';
import { LAST_CHANGES_FILTER_LABELS } from '../../constants';

interface DiffOverviewProps {
    entitiesDiff: number;
    diffIcon: React.FC<React.SVGProps<SVGSVGElement>>;
    diffColor: string;
    diffText: string;
    lastChangeFilter: string;
}

const DiffOverview: FC<DiffOverviewProps> = ({ entitiesDiff, diffIcon, diffColor, diffText, lastChangeFilter }) => {
    return (
        <div className="h-[24px] flex gap-[8px] items-center">
            <div className="h-full px-[8px] flex items-center gap-[4px] rounded border">
                <Icon icon={diffIcon} size={16} className={`text-${diffColor}`} />
                <div className="text-12 font-sf">{bigNumberToText(entitiesDiff, false, 0)}</div>
            </div>
            <div className="h-full flex items-center gap-[4px]">
                <div className="text-13 font-sf text-sf-gray-500">{`Entities ${diffText} in the`}</div>
                <div className="text-13 font-sf text-sf-gray-800 font-medium">{lastChangeFilter}</div>
            </div>
        </div>
    );
};

const InsightOverviewCard = () => {
    const { selectedInsight, insightsData, selectedLastChangesFilter } = useIspmContext();
    const { insightsStatistics, insightsStatisticsDiff, resolvedInsightsStatisticsDiff } = insightsData ?? {};

    const addedEntities =
        selectedInsight && insightsStatisticsDiff
            ? insightsStatisticsDiff?.[selectedInsight]?.added - insightsStatisticsDiff?.[selectedInsight]?.removed
            : 0;
    const resolvedEntities =
        selectedInsight && resolvedInsightsStatisticsDiff
            ? resolvedInsightsStatisticsDiff[selectedInsight].added -
              resolvedInsightsStatisticsDiff[selectedInsight].removed
            : 0;
    const entitiesCount = insightsStatistics?.[selectedInsight ?? ''];
    const riskIndicator = getRiskIndicatorByRiskRule(selectedInsight ?? '');
    const severity = SEVERITIES_INFO[riskIndicator?.severity ?? RISK_SEVERITY.SECURE];
    const insightIcon = getRiskIcon(selectedInsight ?? '');
    return (
        <div className="h-full w-full pt-10 pb-4 px-4 rounded-xl border flex flex-col gap-4">
            <div className="flex h-8 w-full justify-between">
                <div className="flex items-center gap-1">
                    {insightIcon && <Icon icon={insightIcon} size={32} />}
                    <div className="text-[18px] text-sf-gray-700 font-bold font-sf-poppins">
                        {bigNumberToText(entitiesCount, false)}
                    </div>
                </div>
                <div className="flex items-center h-6 gap-1">
                    <Icon
                        className={`p-[6px] bg-sf-gray-50 rounded self-center text-${severity.color}`}
                        icon={severity.icon}
                        size={12}
                    />
                    <div className={`text-12 font-sf text-${severity.color}`}>{severity.label}</div>
                </div>
            </div>
            <div className="flex flex-col gap-[8px]">
                <DiffOverview
                    entitiesDiff={addedEntities ?? 0}
                    diffIcon={Icons.ArrowTrendUp}
                    diffColor="sf-negative-500"
                    diffText="added"
                    lastChangeFilter={LAST_CHANGES_FILTER_LABELS[selectedLastChangesFilter]}
                />
                <DiffOverview
                    entitiesDiff={resolvedEntities ?? 0}
                    diffIcon={Icons.ArrowTrendDown}
                    diffColor="sf-positive-500"
                    diffText="resolved"
                    lastChangeFilter={LAST_CHANGES_FILTER_LABELS[selectedLastChangesFilter]}
                />
            </div>
        </div>
    );
};

export default InsightOverviewCard;
